import { Button } from '@fluentui/react-components';
import { ChevronDown20Regular, ChevronRight20Regular } from '@fluentui/react-icons';
import React, { ReactNode, useState } from 'react';
import './Expandable.module.css';

export interface ActionItemProps {
  icon?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  content?: ReactNode;
  appearance?: 'primary' | 'secondary' | 'outline' | 'subtle' | 'transparent';
  'data-testid'?: string;
}

export interface ExpandableProps {
  header: string;
  actions?: ActionItemProps[];
  extraContent?: ReactNode;
  onOpenStateChange?: (isExpanded: boolean) => void;
  expanded?: boolean;
  children: ReactNode;
}

export const Expandable: React.FC<ExpandableProps> = ({
  header,
  actions,
  extraContent,
  children,
  onOpenStateChange,
  expanded,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(expanded ?? false);

  const toggleOpenState = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);

    if (onOpenStateChange) {
      onOpenStateChange(newState);
    }
  };

  return (
    <div
      className="expandable"
      tabIndex={0}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
          toggleOpenState();
        }
      }}
    >
      <div className="expandableHeader">
        <div
          data-testid="expandableHeaderOpenIndicator"
          className="expandableHeaderOpenIndicator"
          onClick={() => toggleOpenState()}
        >
          {isExpanded && <ChevronDown20Regular />}
          {!isExpanded && <ChevronRight20Regular />}
        </div>
        <div
          className="expandableHeaderLabel"
          onClick={() => toggleOpenState()}
        >
          {header}
        </div>
        {extraContent && <>{extraContent}</>}
        {actions && actions.length > 0 && (
          <div className="expandableHeaderActions">
            {actions.map((action, index) => (
              <Button
                key={index}
                appearance={action.appearance ?? 'transparent'}
                icon={action.icon ? { children: action.icon } : undefined}
                onClick={action.onClick}
                disabled={action.disabled}
                data-testid={action['data-testid']}
              >
                {action.content}
              </Button>
            ))}
          </div>
        )}
      </div>
      {isExpanded && <div className="expandableBody">{children}</div>}
    </div>
  );
};
